/**
 * Store MobX centralisé pour gérer TOUT l'état des onglets :
 * - État métier (enabled/disabled)
 * - État UI (mode desktop/mobile, onglets/dropdown)
 * - Synchronisation automatique du DOM (onglets + dropdown)
 * - Réactivité responsive
 */

import { makeAutoObservable, reaction } from 'mobx';
import { storeEvents, EVENTS } from './storeEvents.js';
import { logger } from '../../utils/debug/logger.js';

class TabStateStore {
  constructor() {
    // État métier des onglets
    this.tabStates = {
      tab1: { enabled: true, label: 'Éventail' },
      tab2: { enabled: false, label: 'Carte' },
      tab3: { enabled: false, label: 'Arbre' },
      tab4: { enabled: false, label: 'Chronologie' },
      tab5: { enabled: false, label: 'Stats' },
      tab6: { enabled: false, label: 'Personnes' },
      // Photos est strictement GEDCOM-scoped (option B) : un upload doit
      // cibler le groupe du GEDCOM courant. Tant qu'aucun GEDCOM n'est
      // chargé, on n'a ni groupe ni contexte → l'onglet reste désactivé,
      // au même titre que tab2-tab6. enableAllTabs() le ré-active à
      // EVENTS.CACHE.BUILT.
      tab7: { enabled: false, label: 'Photos' },
      // Fusion liste les arbres via files-service — pas besoin d'un
      // GEDCOM courant. Activé en même temps que les autres pour rester
      // cohérent avec le pattern existant (tout sauf tab1 disabled
      // avant import).
      tab8: { enabled: false, label: 'Fusion' }
    };
    
    // État global métier
    this.isProcessing = false;
    this.isGeocoding = false;
    
    // Onglet actif
    this.activeTabId = 'tab1';
    
    // État UI responsive
    this.displayMode = 'tabs'; // 'tabs' | 'dropdown'
    this.containerWidth = this.getCurrentContainerWidth();
    
    // Éléments DOM
    this.tabNavElement = null;
    this.leftContainerElement = null;
    this.dropdownElement = null;
    
    makeAutoObservable(this);
    
    // Initialisation
    this.initializeDOM();
    this.setupEventListeners();
    this.setupResponsiveReactions();
    
    logger.ui.debug('📊 TabStateStore centralisé initialisé');
  }
  
  // === INITIALISATION ===
  
  initializeDOM() {
    // Attendre que le DOM soit prêt
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.findDOMElements());
    } else {
      this.findDOMElements();
    }
  }
  
  findDOMElements() {
    this.tabNavElement = document.querySelector('#tab-nav');
    this.leftContainerElement = document.querySelector('#left-container');
    
    if (!this.tabNavElement || !this.leftContainerElement) {
      logger.ui.warn('📊 TabStateStore: Éléments DOM requis non trouvés');
      return;
    }
    
    // Détecter l'onglet actif initial
    const activeTab = this.tabNavElement.querySelector('.nav-link.active');
    if (activeTab) {
      const href = activeTab.getAttribute('href');
      if (href) {
        this.activeTabId = href.replace('#', '');
        logger.ui.debug(`📊 Onglet actif initial détecté: ${this.activeTabId}`);
      }
    }
    
    // Créer la dropdown immédiatement
    this.createDropdown();
    
    // Synchroniser l'état initial
    this.updateDisplayMode();
    this.syncAllDOM();
    
    logger.ui.debug('📊 TabStateStore: Éléments DOM initialisés');
  }
  
  setupEventListeners() {
    // Événements métier GEDCOM
    storeEvents.subscribe(EVENTS.IMPORT.STARTED, () => {
      logger.ui.debug('📊 Import GEDCOM démarré - désactivation des onglets');
      this.setProcessing(true);
      this.disableAllTabsExceptFan();
    });
    
    storeEvents.subscribe(EVENTS.CACHE.BUILT, () => {
      logger.ui.debug('📊 Cache GEDCOM construit - activation des onglets');
      this.setProcessing(false);
      this.enableAllTabs();
    });

    // Verrou Photos: l'onglet ne s'active QUE quand un GEDCOM est anchored
    // dans files-service (currentFileId non null). Sans ça l'utilisateur
    // pourrait ouvrir Photos sur un GEDCOM local non sauvegardé et le
    // photosTabManager n'aurait pas de primary_group_id pour scoper les
    // uploads — Phase 4.7 décision.
    storeEvents.subscribe(EVENTS.FILE.CURRENT_CHANGED, ({ fileId }) => {
      const enabled = !!fileId && !this.isProcessing;
      this.setTabEnabled('tab7', enabled);
    });
    
    // Géocodage
    storeEvents.subscribe(EVENTS.GEOCODING?.STARTED || 'geocoding:started', () => {
      logger.ui.debug('📊 Géocodage démarré - désactivation onglet Carte');
      this.setGeocoding(true);
      this.setTabEnabled('tab2', false);
    });
    
    storeEvents.subscribe(EVENTS.GEOCODING?.COMPLETED || 'geocoding:completed', () => {
      logger.ui.debug('📊 Géocodage terminé - réactivation onglet Carte');
      this.setGeocoding(false);
      if (!this.isProcessing) {
        this.setTabEnabled('tab2', true);
      }
    });
    
    // Écouter le redimensionnement avec throttling
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateContainerWidth();
      }, 100);
    });
    
    // Écouter les changements d'onglets Bootstrap
    document.addEventListener('shown.bs.tab', (event) => {
      const targetHref = event.target.getAttribute('href');
      if (targetHref) {
        const tabId = targetHref.replace('#', '');
        logger.ui.debug(`📊 Onglet activé via Bootstrap: ${tabId}`);
        this.setActiveTab(tabId);
      }
    });
  }
  
  setupResponsiveReactions() {
    // Réaction automatique au changement de largeur
    reaction(
      () => this.containerWidth,
      (width) => {
        const newMode = width < 1280 ? 'dropdown' : 'tabs';
        if (newMode !== this.displayMode) {
          logger.ui.debug(`📊 Changement mode responsive: ${this.displayMode} → ${newMode} (largeur: ${width}px)`);
          this.displayMode = newMode;
          
          // Synchronisation forcée immédiate après changement de mode
          logger.ui.debug('📊 Synchronisation forcée après changement de mode');
          setTimeout(() => {
            this.syncAllDOM();
            logger.ui.debug('📊 Synchronisation forcée terminée');
          }, 0);
        }
      }
    );
    
    // Réaction automatique aux changements d'état des onglets
    reaction(
      () => Object.values(this.tabStates).map(state => state.enabled),
      () => {
        logger.ui.debug('📊 État des onglets changé, synchronisation DOM');
        this.syncAllDOM();
      }
    );
    
    // Réaction au changement de mode d'affichage
    reaction(
      () => this.displayMode,
      (mode) => {
        logger.ui.debug(`📊 Mode d'affichage changé: ${mode}`);
        // Double synchronisation pour s'assurer que le nouveau mode est correctement synchronisé
        this.syncAllDOM();
        setTimeout(() => {
          this.syncAllDOM();
          logger.ui.debug('📊 Synchronisation différée du mode terminée');
        }, 100);
      }
    );
  }
  
  // === ACTIONS MÉTIER ===
  
  setProcessing(processing) {
    this.isProcessing = processing;
    logger.ui.debug(`📊 Processing = ${processing}`);
  }
  
  setGeocoding(geocoding) {
    this.isGeocoding = geocoding;
    logger.ui.debug(`📊 Geocoding = ${geocoding}`);
  }
  
  setTabEnabled(tabId, enabled) {
    if (this.tabStates[tabId]) {
      const wasEnabled = this.tabStates[tabId].enabled;
      this.tabStates[tabId].enabled = enabled;
      
      if (wasEnabled !== enabled) {
        logger.ui.debug(`📊 ${tabId} ${enabled ? 'activé' : 'désactivé'}`);
      }
    } else {
      logger.ui.warn(`📊 Tentative de modifier un onglet inexistant: ${tabId}`);
    }
  }
  
  setActiveTab(tabId) {
    if (this.tabStates[tabId] && this.activeTabId !== tabId) {
      const oldTabId = this.activeTabId;
      this.activeTabId = tabId;
      logger.ui.debug(`📊 Onglet actif: ${oldTabId} → ${tabId}`);
      
      // Mettre à jour le texte du dropdown
      this.updateDropdownText();
      
      // Synchroniser l'état actif dans le dropdown
      this.syncActiveStateInDropdown();
    }
  }
  
  updateDropdownText() {
    if (!this.dropdownElement) return;
    
    const buttonText = this.dropdownElement.querySelector('#current-tab-text');
    if (buttonText && this.tabStates[this.activeTabId]) {
      buttonText.textContent = this.tabStates[this.activeTabId].label;
      logger.ui.debug(`📊 Texte dropdown mis à jour: ${this.tabStates[this.activeTabId].label}`);
    }
  }
  
  syncActiveStateInDropdown() {
    if (!this.dropdownElement) return;
    
    const dropdownMenu = this.dropdownElement.querySelector('#tabs-dropdown-menu');
    if (!dropdownMenu) return;
    
    // Retirer la classe active de tous les items
    dropdownMenu.querySelectorAll('.dropdown-item').forEach(item => {
      item.classList.remove('active');
    });
    
    // Ajouter la classe active à l'item correspondant
    const activeItem = dropdownMenu.querySelector(`a[data-tab-id="${this.activeTabId}"]`);
    if (activeItem) {
      activeItem.classList.add('active');
      logger.ui.debug(`📊 Item dropdown ${this.activeTabId} marqué comme actif`);
    }
  }
  
  enableAllTabs() {
    logger.ui.debug('📊 Activation des onglets (sauf tab7 Photos qui suit currentFileId)');
    Object.keys(this.tabStates).forEach(tabId => {
      // Cas spécial : ne pas réactiver la carte si géocodage en cours
      if (tabId === 'tab2' && this.isGeocoding) {
        logger.ui.debug('📊 Carte reste désactivée (géocodage en cours)');
        return;
      }
      // tab7 (Photos) est gouverné par FILE.CURRENT_CHANGED, pas par
      // CACHE.BUILT : pas de photos tant que le GEDCOM n'est pas
      // sauvegardé dans files-service.
      if (tabId === 'tab7') return;
      this.tabStates[tabId].enabled = true;
    });
  }
  
  disableAllTabsExceptFan() {
    logger.ui.debug('📊 Désactivation de tous les onglets sauf Éventail');
    Object.keys(this.tabStates).forEach(tabId => {
      if (tabId !== 'tab1') {
        this.tabStates[tabId].enabled = false;
      }
    });
  }
  
  // === ACTIONS UI ===
  
  updateContainerWidth() {
    this.containerWidth = this.getCurrentContainerWidth();
  }
  
  getCurrentContainerWidth() {
    if (this.leftContainerElement) {
      return this.leftContainerElement.getBoundingClientRect().width;
    }
    return window.innerWidth;
  }
  
  updateDisplayMode() {
    this.containerWidth = this.getCurrentContainerWidth();
    this.displayMode = this.containerWidth < 1280 ? 'dropdown' : 'tabs';
  }
  
  // === CRÉATION DROPDOWN ===
  
  createDropdown() {
    if (!this.tabNavElement || !this.leftContainerElement) {
      logger.ui.warn('📊 Impossible de créer la dropdown : éléments DOM manquants');
      return;
    }
    
    logger.ui.debug('🔧 Création de la dropdown');
    
    // Créer la structure du dropdown
    const dropdownContainer = document.createElement('div');
    dropdownContainer.className = 'dropdown tab-dropdown';
    
    // Bouton toggle
    const button = document.createElement('button');
    button.className = 'btn dropdown-toggle';
    button.setAttribute('type', 'button');
    button.id = 'tabsDropdownMenu';
    
    // Texte du bouton
    const buttonText = document.createElement('span');
    buttonText.id = 'current-tab-text';
    
    // Trouver l'onglet actif pour le texte initial
    const activeTab = this.tabNavElement.querySelector('.nav-link.active .tab-label');
    buttonText.textContent = activeTab ? activeTab.textContent : 'Éventail';
    
    button.appendChild(buttonText);
    
    // Menu dropdown
    const dropdownMenu = document.createElement('ul');
    dropdownMenu.className = 'dropdown-menu';
    dropdownMenu.setAttribute('aria-labelledby', 'tabsDropdownMenu');
    dropdownMenu.id = 'tabs-dropdown-menu';
    
    // Peupler le menu
    this.populateDropdownMenu(dropdownMenu, buttonText);
    
    // Gestionnaire de clic pour ouvrir/fermer
    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      const isVisible = dropdownMenu.classList.contains('show');
      if (isVisible) {
        dropdownMenu.classList.remove('show');
        dropdownMenu.style.display = 'none';
        button.setAttribute('aria-expanded', 'false');
      } else {
        dropdownMenu.classList.add('show');
        dropdownMenu.style.display = 'block';
        button.setAttribute('aria-expanded', 'true');
      }
    });
    
    // Fermer au clic en dehors
    document.addEventListener('click', (e) => {
      if (dropdownMenu && !button.contains(e.target) && !dropdownMenu.contains(e.target)) {
        dropdownMenu.classList.remove('show');
        dropdownMenu.style.display = 'none';
        button.setAttribute('aria-expanded', 'false');
      }
    });
    
    // Assembler
    dropdownContainer.appendChild(button);
    dropdownContainer.appendChild(dropdownMenu);
    
    // Insérer dans le DOM
    this.leftContainerElement.insertBefore(dropdownContainer, this.tabNavElement);
    
    this.dropdownElement = dropdownContainer;
    logger.ui.debug('✅ Dropdown créée');
  }
  
  populateDropdownMenu(dropdownMenu, buttonText) {
    const tabItems = this.tabNavElement.querySelectorAll('.nav-item');
    
    tabItems.forEach(item => {
      const link = item.querySelector('.nav-link');
      if (!link) return;
      
      const labelElement = link.querySelector('.tab-label');
      const label = labelElement ? labelElement.textContent : link.textContent.trim();
      const href = link.getAttribute('href') || '#';
      const tabId = href.replace('#', '');
      
      // Créer l'élément de dropdown
      const listItem = document.createElement('li');
      const anchor = document.createElement('a');
      anchor.className = 'dropdown-item';
      anchor.textContent = label;
      anchor.href = href;
      anchor.setAttribute('data-tab-id', tabId);
      
      // Gestionnaire de clic
      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        logger.ui.debug(`🎯 Clic dropdown: ${label} (${tabId})`);
        
        // Vérifier si l'onglet est activé
        if (!this.isTabEnabled(tabId)) {
          logger.ui.debug(`❌ Onglet ${tabId} désactivé, clic ignoré`);
          return;
        }
        
        // Activer l'onglet original
        const originalLink = this.tabNavElement.querySelector(`a[href="${href}"]`);
        if (originalLink) {
          originalLink.click();
          
          // Mettre à jour le texte du dropdown
          buttonText.textContent = label;
          
          // Fermer le dropdown
          const menu = document.getElementById('tabs-dropdown-menu');
          if (menu) {
            menu.classList.remove('show');
            menu.style.display = 'none';
            document.getElementById('tabsDropdownMenu')?.setAttribute('aria-expanded', 'false');
          }
        }
      });
      
      listItem.appendChild(anchor);
      dropdownMenu.appendChild(listItem);
    });
  }
  
  // === SYNCHRONISATION DOM ===
  
  syncAllDOM() {
    logger.ui.debug('📊 syncAllDOM: Début synchronisation complète');
    this.syncTabsDOM();
    this.syncDropdownDOM();
    this.syncDisplayModeDOM();
    
    // Synchroniser aussi l'onglet actif
    this.updateDropdownText();
    this.syncActiveStateInDropdown();
    
    // Debug après synchronisation
    setTimeout(() => {
      this.debugDOMSync();
    }, 50);
    
    logger.ui.debug('📊 syncAllDOM: Fin synchronisation complète');
  }
  
  syncTabsDOM() {
    if (!this.tabNavElement) return;
    
    Object.entries(this.tabStates).forEach(([tabId, state]) => {
      const tabLink = this.tabNavElement.querySelector(`a[href="#${tabId}"]`);
      if (tabLink) {
        tabLink.classList.toggle('disabled', !state.enabled);
        tabLink.setAttribute('aria-disabled', state.enabled ? 'false' : 'true');
        tabLink.setAttribute('tabindex', state.enabled ? '0' : '-1');
      }
    });
  }
  
  syncDropdownDOM() {
    if (!this.dropdownElement) {
      logger.ui.debug('📊 syncDropdownDOM: dropdownElement non trouvé');
      return;
    }
    
    const dropdownMenu = this.dropdownElement.querySelector('#tabs-dropdown-menu');
    if (!dropdownMenu) {
      logger.ui.debug('📊 syncDropdownDOM: dropdownMenu non trouvé');
      return;
    }
    
    logger.ui.debug('📊 syncDropdownDOM: Début synchronisation dropdown');
    
    Object.entries(this.tabStates).forEach(([tabId, state]) => {
      const dropdownItem = dropdownMenu.querySelector(`a[data-tab-id="${tabId}"]`);
      if (dropdownItem) {
        const wasDisabled = dropdownItem.classList.contains('disabled');
        const shouldBeDisabled = !state.enabled;
        
        dropdownItem.classList.toggle('disabled', shouldBeDisabled);
        dropdownItem.style.pointerEvents = state.enabled ? 'auto' : 'none';
        dropdownItem.style.opacity = state.enabled ? '1' : '0.6';
        
        if (wasDisabled !== shouldBeDisabled) {
          logger.ui.debug(`📊 syncDropdownDOM: ${tabId} ${shouldBeDisabled ? 'désactivé' : 'activé'} dans dropdown`);
        }
      } else {
        logger.ui.debug(`📊 syncDropdownDOM: Item ${tabId} non trouvé dans dropdown`);
      }
    });
    
    logger.ui.debug('📊 syncDropdownDOM: Fin synchronisation dropdown');
  }
  
  syncDisplayModeDOM() {
    // Le CSS Container Query gère déjà l'affichage onglets/dropdown
    // Pas besoin de manipulation manuelle
  }
  
  // === GETTERS ===
  
  isTabEnabled(tabId) {
    return this.tabStates[tabId]?.enabled || false;
  }
  
  get enabledTabs() {
    return Object.entries(this.tabStates)
      .filter(([_, state]) => state.enabled)
      .map(([tabId, _]) => tabId);
  }
  
  get enabledCount() {
    return this.enabledTabs.length;
  }
  
  // === DEBUG ===
  
  logState() {
    logger.ui.debug('📊 TabStateStore - État complet:');
    logger.ui.debug('Processing:', this.isProcessing);
    logger.ui.debug('Geocoding:', this.isGeocoding);
    logger.ui.debug('Display Mode:', this.displayMode);
    logger.ui.debug('Container Width:', this.containerWidth);
    Object.entries(this.tabStates).forEach(([tabId, state]) => {
      logger.ui.debug(`${tabId}: ${state.enabled ? '✅ activé' : '❌ désactivé'} (${state.label})`);
    });
  }
  
  debugDOMSync() {
    logger.ui.debug('🔍 TabStateStore - Debug DOM Sync:');
    
    // État des onglets dans le DOM
    logger.ui.debug('=== ONGLETS ===');
    if (this.tabNavElement) {
      Object.entries(this.tabStates).forEach(([tabId, state]) => {
        const tabLink = this.tabNavElement.querySelector(`a[href="#${tabId}"]`);
        if (tabLink) {
          const isDisabledInDOM = tabLink.classList.contains('disabled');
          const shouldBeDisabled = !state.enabled;
          logger.ui.debug(`${tabId}: État=${state.enabled ? 'activé' : 'désactivé'}, DOM=${isDisabledInDOM ? 'désactivé' : 'activé'}, Sync=${isDisabledInDOM === shouldBeDisabled ? '✅' : '❌'}`);
        } else {
          logger.ui.debug(`${tabId}: ❌ Onglet non trouvé dans DOM`);
        }
      });
    } else {
      logger.ui.debug('❌ tabNavElement non trouvé');
    }
    
    // État des items dropdown dans le DOM
    logger.ui.debug('=== DROPDOWN ===');
    if (this.dropdownElement) {
      const dropdownMenu = this.dropdownElement.querySelector('#tabs-dropdown-menu');
      if (dropdownMenu) {
        Object.entries(this.tabStates).forEach(([tabId, state]) => {
          const dropdownItem = dropdownMenu.querySelector(`a[data-tab-id="${tabId}"]`);
          if (dropdownItem) {
            const isDisabledInDOM = dropdownItem.classList.contains('disabled');
            const shouldBeDisabled = !state.enabled;
            logger.ui.debug(`${tabId}: État=${state.enabled ? 'activé' : 'désactivé'}, DOM=${isDisabledInDOM ? 'désactivé' : 'activé'}, Sync=${isDisabledInDOM === shouldBeDisabled ? '✅' : '❌'}`);
          } else {
            logger.ui.debug(`${tabId}: ❌ Item dropdown non trouvé dans DOM`);
          }
        });
      } else {
        logger.ui.debug('❌ dropdownMenu non trouvé');
      }
    } else {
      logger.ui.debug('❌ dropdownElement non trouvé');
    }
    
  }
}

// Instance unique du store
export const tabStateStore = new TabStateStore();

// Exposer globalement pour debug
if (typeof window !== 'undefined') {
  window.tabStateStore = tabStateStore;
  
  // Méthodes utilitaires pour debugging depuis la console
  window.debugTabs = () => {
    tabStateStore.debugDOMSync();
  };
  
  window.forceSyncTabs = () => {
    logger.ui.debug('🔧 Synchronisation forcée depuis console');
    tabStateStore.syncAllDOM();
  };
  
  window.logTabState = () => {
    tabStateStore.logState();
  };
}